export interface NewRelicBrowserAgent {
  noticeError(error: Error, customAttributes?: Record<string, string | number | boolean>): void;
  addPageAction(name: string, attributes?: Record<string, unknown>): void;
  setCustomAttribute(name: string, value: string | number | boolean): void;
}

function getAgent(): NewRelicBrowserAgent | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { newrelic?: NewRelicBrowserAgent }).newrelic;
}

/**
 * Report an error to the New Relic browser agent. Returns true only when a live
 * agent was present and called, so callers can decide honestly whether to tell
 * the user we have been notified. No global Window augmentation here, to avoid
 * colliding with the customer-portal-local window.newrelic declaration.
 */
export function reportErrorToNewRelic(
  error: Error,
  attributes: Record<string, string | number | boolean>,
): boolean {
  const agent = getAgent();
  if (!agent) return false;
  try {
    agent.noticeError(error, attributes);
  } catch {
    // The NR browser agent is third-party injected code that can throw (a broken
    // ad-block stub, a serialisation error). This runs inside the outermost
    // componentDidCatch, so a propagating throw would unmount the tree and
    // white-screen the fallback. Swallow it and report that we did not notify.
    return false;
  }
  return true;
}

/**
 * Set a session-level custom attribute on the New Relic browser agent, so every
 * subsequent event (JavaScriptError, PageView, BrowserInteraction, AjaxRequest)
 * carries it and can be faceted in NRQL. Guarded like reportErrorToNewRelic:
 * the agent is third-party injected code that may be absent or throw.
 */
export function setNewRelicCustomAttribute(
  name: string,
  value: string | number | boolean,
): boolean {
  const agent = getAgent();
  if (!agent) return false;
  try {
    agent.setCustomAttribute(name, value);
  } catch {
    return false;
  }
  return true;
}

/**
 * Record a page action on the New Relic browser agent, for an event that is not an uncaught error
 * (a handled failure worth seeing in production). The agent does not collect console output in this
 * deployment, so a page action is how a handled failure becomes visible to engineering. Guarded like
 * the helpers above, and it returns true only when a live agent was called. Attributes are for a
 * reason or a constant label: never an identifier, a token or an error object.
 */
export function reportPageActionToNewRelic(
  name: string,
  attributes?: Record<string, string | number | boolean>,
): boolean {
  const agent = getAgent();
  if (!agent) return false;
  try {
    agent.addPageAction(name, attributes);
  } catch {
    return false;
  }
  return true;
}
